import React, { useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Play } from 'lucide-react';
import { getYoutubeVideoId } from '@/components/drill-library/services/youtube';

interface ConorHarrisVideoPlayerProps {
  videoUrl?: string;
  title?: string;
}

const DEFAULT_YOUTUBE_URL = 'https://youtu.be/dh3zFm21sVQ';

export const ConorHarrisVideoPlayer: React.FC<ConorHarrisVideoPlayerProps> = ({
  videoUrl = DEFAULT_YOUTUBE_URL,
  title = "What Your Posture Assessment Means & How to Fix It",
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const youtubeId = getYoutubeVideoId(videoUrl);

  const isDirectVideo =
    videoUrl.endsWith('.mp4') ||
    videoUrl.endsWith('.webm') ||
    videoUrl.includes('direct=1') ||
    videoUrl.includes('firebasestorage') ||
    videoUrl.includes('.mp4?');

  const getEmbedSrc = () => {
    if (youtubeId) {
      return `https://www.youtube.com/embed/${youtubeId}?autoplay=1&muted=1&playsinline=1&rel=0&modestbranding=1&enablejsapi=1`;
    }
    const separator = videoUrl.includes('?') ? '&' : '?';
    return `${videoUrl}${separator}autoplay=1&muted=1&playsinline=1`;
  };

  return (
    <Card className="light overflow-hidden border-line bg-surface text-fg shadow-md">
      <CardContent className="p-4 sm:p-6 flex flex-col gap-4">
        <div className="flex flex-col gap-1 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 self-center sm:self-start px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider">
            <Play className="h-3.5 w-3.5 fill-primary" />
            Watch First
          </div>
          <h3 className="font-bold text-lg sm:text-xl text-fg font-['Poppins',sans-serif] mt-1">
            {title}
          </h3>
          <p className="text-base sm:text-lg text-muted-foreground font-['Roboto',sans-serif] leading-relaxed">
            Conor Harris breaks down what your photos reveal. A few more details help us match the support that fits you.
          </p>
        </div>

        <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-fg shadow-lg border border-line">
          {isDirectVideo ? (
            <video
              ref={videoRef}
              src={videoUrl}
              autoPlay
              muted
              loop
              controls
              playsInline
              className="w-full h-full object-cover pointer-events-auto"
            />
          ) : (
            <iframe
              src={getEmbedSrc()}
              title="Conor Harris Posture Assessment Video"
              className="absolute inset-0 w-full h-full border-0 pointer-events-auto"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConorHarrisVideoPlayer;
